import { Form } from './form';
import { PayPalView } from '../view/paypal-view';
import { FormTypes } from './form-types';
import { Config } from '../config';
import { Order } from '../config/order';
import { PaymentType } from '../providers/payment-type';
import { Product } from '../config/product';
import { DonationView } from '../view/donation-view';
import { ProductType } from '../config/product-type';
import { DonationFrequencyType } from '../config/donation-frequency-type';

declare const process: any;

export class PaypalForm extends Form {

    view: PayPalView = new PayPalView();
    type = FormTypes.Paypal;
    donationView: DonationView = new DonationView();


    constructor(step: number | null = null) {
        super();
        this.step = step;
    }

    async init(config: Config, callback: any) {
        await super.init(config, callback);
        this.config = config;
        return Promise.resolve();
    }

    async render() {
        if (this.config.products && this.config.products.length === 0) {
            return Promise.resolve();
        }
        if (this.doesSubscriptionExist(this.config)) {
            return Promise.resolve();
        }
        const payPalProperties = this.getPayPalProperties();
        if (payPalProperties.length === 0) {
            return Promise.resolve();            
        }
        this.view.createPayPalForm(payPalProperties, this.getPayPalFormUrl());
        this.view.addPaypalButton();
        if (this.isDonationProduct(this.config.products)) {
            this.addPlanIntervalChangeEvent();
        }

        if (this.step) {
            this.hideForm();
        }
        return Promise.resolve();
    }

    getPayPalProperties() {
        let paypalPaymentProps: any[] = [];
        const paymentType = this.getPaymentType(this.config);
        if (!paymentType) {
            return paypalPaymentProps;
        }
        switch (paymentType) {
            case ProductType.SINGLE_PAYMENT:
                paypalPaymentProps = this.singlePaymentProps;
                break;
            case `${ProductType.DONATION}_${DonationFrequencyType.ONE_TIME}`:
                paypalPaymentProps = this.singlePaymentProps;
                break;
                case `${ProductType.DONATION}_recurring`:
                paypalPaymentProps = this.subscriptionProps;
                break;
            case ProductType.NO_PAYMENT:
                paypalPaymentProps = this.singlePaymentProps;
                break;
            default:
                break;
        }
        return paypalPaymentProps;
    }

    doesSubscriptionExist(config: any) {
        return config.products.find((product: Product) => product.type === ProductType.SUBSCRIPTION);
    }

    getPaymentType(config: any) {
        const products: Product[] = this.config.products;
        const areAllProductsSinglePayment = this.areAllProductsOfSameType(products, ProductType.SINGLE_PAYMENT);
        if (areAllProductsSinglePayment) {
            return ProductType.SINGLE_PAYMENT;
        }
        if (this.isDonationProduct(products)) {
            const planInterval = this.donationView.getSubscriptionPlan();
            return planInterval === DonationFrequencyType.ONE_TIME ?
                `${ProductType.DONATION}_${DonationFrequencyType.ONE_TIME}` :
                `${ProductType.DONATION}_recurring`;
        }
        if (products.find((product: Product) => product.type === ProductType.NO_PAYMENT)) {
            return ProductType.NO_PAYMENT;
        }
        return null;
    }

    areAllProductsOfSameType(products: Product[], paymentType: string) {
        for (let i = 0; i < products.length; i++) {
            if (products[i].type !== paymentType) {
                return false;
            }
        }
        return true;
    }

    get singlePaymentProps() {
        return [
            {name: 'cmd', value: '_xclick'},
            {name: 'business', value: this.config.forms.paypal.email},
            {name: 'notify_url', value: this.getPayPalNotifyUrl()},
            {name: 'return', value: this.getRedirectUrl(this.config.settings.redirectUrl)},
            {name: 'amount', value: ''},
            {name: 'email', value: ''},
            {name: 'first_name', value: ''},
            {name: 'last_name', value: ''},
            {name: 'item_name', value: ''},
            {name: 'currency_code', value: this.config.settings.currency},
            {name: 'custom', value: ''}
        ];
    }

    get subscriptionProps() {
        return [
            {name: 'cmd', value: '_xclick-subscriptions'},
            {name: 'business', value: this.config.forms.paypal.email},
            {name: 'notify_url', value: this.getPayPalNotifyUrl()},
            {name: 'return', value: this.getRedirectUrl(this.config.settings.redirectUrl)},
            {name: 'rm', value: '1'},
            {name: 'a3', value: ''},
            {name: 't3', value: ''},
            {name: 'p3', value: '1'},
            {name: 'src', value: '1'},
            {name: 'sra', value: '1'},
            {name: 'email', value: ''},
            {name: 'first_name', value: ''},
            {name: 'last_name', value: ''},
            {name: 'item_name', value: ''},
            {name: 'currency_code', value: this.config.settings.currency},
            {name: 'no_note', value: '1'},
            {name: 'custom', value: ''}
        ];
    }

    get singlePaymentSubscriptionProps() {
        return [
            {name: 'cmd', value: '_xclick-subscriptions'},
            {name: 'business', value: this.config.forms.paypal.email},
            {name: 'notify_url', value: this.getPayPalNotifyUrl()},
            {name: 'return', value: this.getRedirectUrl(this.config.settings.redirectUrl)},
            {name: 'rm', value: '1'},
            {name: 'a1', value: ''},
            {name: 't1', value: 'D'},
            {name: 'p1', value: '1'},
            {name: 'a3', value: ''},
            {name: 't3', value: ''},
            {name: 'p3', value: '1'},
            {name: 'src', value: '1'},
            {name: 'sra', value: '1'},
            {name: 'email', value: ''},
            {name: 'first_name', value: ''},
            {name: 'last_name', value: ''},
            {name: 'item_name', value: ''},
            {name: 'currency_code', value: this.config.settings.currency},
            {name: 'no_note', value: '1'},
            {name: 'custom', value: ''}
        ];
    }

    isDonationProduct(products: Product[]) {
        return products.find((product: Product) => product.type === ProductType.DONATION);
    }

    addPlanIntervalChangeEvent() {
        const planIntervalElement = this.donationView.getPlanIntervalElement();
        planIntervalElement.addEventListener('change', () => {
            this.view.deletePayPalForm();
            this.view.createPayPalForm(this.getPayPalProperties(), this.getPayPalFormUrl());
        });
    }

    submit(order: Order) {
        order.paymentMethod = {
                    type: PaymentType.PAYPAL,
                    businessEmail: this.config.forms.paypal.email
                };

        return Promise.resolve(order);
    }

    postSubmit(submissionContext: any): Promise<any> {
        const amount = this.config.widget.getAmount();
        const data = submissionContext.params[0];
        const form = document.querySelector('#df_paypalform') as HTMLFormElement;
        this.setFormElement(form, 'email', data.email);
        this.setFormElement(form, 'first_name', data.firstname);
        this.setFormElement(form, 'last_name', data.lastname);
        if (this.getFormElement(form, 'amount')) {
            this.setFormElement(form, 'amount', amount);
        }
        if (this.getFormElement(form, 'a1')) {
            this.setFormElement(form, 'a1', amount);
        }
        if (this.getFormElement(form, 'a3')) {
            this.setFormElement(form, 'a3', amount);
        }
        if (this.getFormElement(form, 't3')) {
            const planInterval = this.donationView.getSubscriptionPlan();
            if (planInterval) {
                this.setFormElement(form, 't3', this.mapPlanInterval(planInterval));
            }
        }
        if (form['custom']) {
            this.setFormElement(form, 'custom', this.getPayPalCustomData(data.email, submissionContext));
        }

        this.setFormElement(form, 'return', submissionContext.redirect);

        if (submissionContext.orderItems) {
            const paypalItems: Array<any> = submissionContext.orderItems.filter((item: any) => {
                return item.hasOwnProperty('productName');
            });
            if (paypalItems.length > 0) {
                this.setFormElement(form, 'item_name', this.getProductName(paypalItems));
            }
        }
        
        this.submitPayPalForm(form, submissionContext)
            .catch(error => this.logger.log(error));
        return Promise.resolve();
    }

    private getPayPalCustomData(email: string, submissionContext: any) {
        const mode = (this.config.forms.paypal.mode && this.config.forms.paypal.mode === 'test') ? 'test' : 'live';
        const quantity = submissionContext.orderItems && submissionContext.orderItems.length > 0 &&
            submissionContext.orderItems[0].quantity !== null && submissionContext.orderItems[0].quantity !== undefined ?
            submissionContext.orderItems[0].quantity : 1;
        const discountId = submissionContext.orderItems && submissionContext.orderItems.length > 0 &&
            submissionContext.orderItems[0].discountId !== null && submissionContext.orderItems[0].discountId !== undefined ?
            submissionContext.orderItems[0].discountId : null;
        const hubSpotDealId =
            submissionContext.hubSpotDealId &&
            submissionContext.hubSpotDealId !== null &&
            submissionContext.hubSpotDealId !== undefined ?
                submissionContext.hubSpotDealId :
                null;

        const customData = {
            mode,
            email,
            discountId: discountId,
            orderId: submissionContext.result.orderId,
            quantity: quantity,
            hubSpotDealId: hubSpotDealId
        };

        return JSON.stringify(customData);
    }

    private getProductName(paypalItems: any[]): string {
        return paypalItems.map(payPalItem => payPalItem.productName).join(', ');
    }

    private getRedirectUrl(redirectUrl: string) {
        return redirectUrl ? redirectUrl : this.config.settings.fallbackRedirectUrl;
    }

    private submitPayPalForm(form: HTMLFormElement, submissionContext: any) {
        this.config.widget.triggerAppEvent('onFormSubmitted', submissionContext);
        form.submit();
        return Promise.resolve();
    }

    private getPayPalNotifyUrl() {
        const host = process.env.API_URL;
        return host + 'webhooks/v2/paypal/' + this.config.user.portalId;
    }

    private getFormElement(form: HTMLFormElement, name: string) {
        return form[name];
    }

    private setFormElement(form: HTMLFormElement, name: string, value: string) {
        form[name].setAttribute('value', value);
    }

    private getPayPalFormUrl() {
        return this.config.forms.paypal.mode && this.config.forms.paypal.mode === 'test' ?
            'https://www.sandbox.paypal.com/cgi-bin/webscr' :
            'https://www.paypal.com/cgi-bin/webscr';
    }

    private mapPlanInterval(planInterval: string) {
        let payPalPlanInterval;
        switch (planInterval) {
            case DonationFrequencyType.ONE_TIME:
                payPalPlanInterval = 'D';
                break;
            case DonationFrequencyType.MONTHLY:
                payPalPlanInterval = 'M';
                break;
            default:
                payPalPlanInterval = 'M';
                break;
        }
        return payPalPlanInterval;
    }

    showForm() {
        this.view.showForm();
    }

    hideForm() {
        this.view.hideForm();
    }

}
